import { DrawerBase } from 'bog-arcade-core-ui/core/DrawerBase';
import { gsap, TimelineLite } from 'gsap';
import { random } from 'bog-arcade-core-ui/helpers';
import { resourceHelper } from '../../../core/resourceHelper';
import { mobileConfig } from '../../../core/applicationConfig';
import {
  DragonBitingAnimation,
  DragonSoulAnimations,
  WallAnimations,
  FishAnimations,
  GameLayers,
  LeafAnimations,
} from '../../../core/enums';
import { bonusOddStyle, multiplierValueStyle, winOddTextStyle } from '../../../core/textStyles';
import { sharedSourceNames } from '../../../core/sourceNames';
import {
  BONUS_TOTAL_ODD,
  FREE_SPIN_COUNT_STATE,
  MULTIPLIER_STATE,
  USER_DATA,
} from '../../../stateManagment';
import { translationKeys } from '../../../core/translation/translation';
import { iframeService } from '../../../services/helperServices/IframeService';
import { FONT_LATO_NAME } from '../../../core/constats';
import { getAmount } from '../../../core/helpers/getAmount';
import { gameAnimations } from '../../../services/helperServices/GameAnimationService';
import { getFixedValue } from '../../../core/helpers/getFixedValue';
import { soundService } from '../../../services/helperServices/SoundService';

export class BaseBackgroundDrawer extends DrawerBase {
  constructor() {
    super();
    this.__animations = {};
    this.__fish_animationIndex = -1;
  }

  setupLayers(layers) {
    this.container = {
      [GameLayers.background]: layers[GameLayers.background],
      [GameLayers.shadow]: layers[GameLayers.shadow],
      [GameLayers.dragonForce]: layers[GameLayers.dragonForce],
      [GameLayers.dragonForceBack]: layers[GameLayers.dragonForceBack],
      [GameLayers.waterfallSpine]: layers[GameLayers.waterfallSpine],
      [GameLayers.waterSpine]: layers[GameLayers.waterSpine],
      [GameLayers.wallSpine]: layers[GameLayers.wallSpine],
      [GameLayers.treesSpine]: layers[GameLayers.treesSpine],
      [GameLayers.plateSpine]: layers[GameLayers.plateSpine],
      [GameLayers.dragonSpine]: layers[GameLayers.dragonSpine],
      [GameLayers.breakingBallSpine]: layers[GameLayers.breakingBallSpine],
      [GameLayers.particles]: layers[GameLayers.particles],
      [GameLayers.allBallsBreakingSpine]: layers[GameLayers.allBallsBreakingSpine],
      [GameLayers.fishSpine]: layers[GameLayers.fishSpine],
      [GameLayers.dragonSoulSpine]: layers[GameLayers.dragonSoulSpine],
      [GameLayers.winOdd]: layers[GameLayers.winOdd],
      [GameLayers.deposit]: layers[GameLayers.deposit],
      [GameLayers.buyBonusButton]: layers[GameLayers.buyBonusButton],
    };
  }

  _setBackground({ name, containerName, position = mobileConfig.staticAnimationPosition, startAnimation = true } = {}) {
    const texture = this.createSpine(resourceHelper.get(name));
    this.setPosition(texture, position);
    if (startAnimation) {
      texture.state.setAnimation(0, 'animation', true);
    }
    this.container[containerName].addChild(texture);
    return texture;
  }

  __setAnimationPromise(texture, name, events, loop = false, clear = false, timeScale = 1) {
    return new Promise(resolve => {
      texture.state.clearTrack();
      texture.state.timeScale = timeScale;
      texture.state.setAnimation(0, name, loop);
      texture.state.addListener({
        ...events,
        complete: function (entry) {
          resolve(entry);
        },
      });
    });
  }

  _setupDragonAnimations(texture) {
    this.__animations = {
      ...this.__animations,
      [DragonBitingAnimation.bonusBall]: events => this.__setAnimationPromise(
        texture,
        DragonBitingAnimation.bonusBall,
        events,
      ),
      [DragonBitingAnimation.break]: events => this.__setAnimationPromise(
        texture,
        DragonBitingAnimation.break,
        events,
        false,
        false,
        2,
      ),
      [DragonBitingAnimation.crack]: events => this.__setAnimationPromise(
        texture,
        DragonBitingAnimation.crack,
        events,
        false,
        false,
        2,
      ),
    };
  }

  _setupBreakingBallAnimations(texture) {
    this.__animations = {
      ...this.__animations,
      breakBallBoom: (events, loop = false) => {
        this.container[GameLayers.breakingBallSpine].visible = true;
        return this.__setAnimationPromise(
          texture,
          'animation',
          events,
          loop,
          false,
          1.2,
        ).then(e => {
          // this.container[GameLayers.breakingBallSpine].visible = false;
          return e;
        });
      },
    };
  }

  _setupParticlesAnimations(texture) {
    this.__animations = {
      ...this.__animations,
      crackParticles: (events, loop = false) => {
        this.container[GameLayers.particles].visible = true;
        return this.__setAnimationPromise(
          texture,
          'animation',
          events,
          loop,
          false,
          1.6,
        ).then(e => {
          // this.container[GameLayers.breakingBallSpine].visible = false;
          return e;
        });
      },
    };
  }

  _setupAnimationMix(texture, animation1, animation2, duration) {
    this.__animations = {
      ...this.__animations,
      setAnimationMix: (animation1, animation2, duration) => texture.stateData.setMix(animation1, animation2, duration),
    };
  }


  _setupDragonSoulAnimations(texture) {
    this.__animations = {
      ...this.__animations,
      [DragonSoulAnimations.loop]: (events, loop = true) => this.__setAnimationPromise(
        texture,
        DragonSoulAnimations.loop,
        events,
        loop,
      ),
      [DragonSoulAnimations.dracarys]: (events, loop = false) => this.__setAnimationPromise(
        texture,
        DragonSoulAnimations.dracarys,
        events,
        loop,
        false,
        1.3,
      ),
      [DragonSoulAnimations.appear]: (events, loop = false) => this.__setAnimationPromise(
        texture,
        DragonSoulAnimations.appear,
        events,
        loop,
      ),
    };
    // texture.stateData.setMix(DragonSoulAnimations.dracarys, DragonSoulAnimations.loop, .01)
    // texture.stateData.setMix(DragonSoulAnimations.loop, DragonSoulAnimations.dracarys, .01);
  }

  _setupWallAnimations(texture) {
    this.__animations = {
      ...this.__animations,
      [WallAnimations.wallLight]: (events, loop = false) => this.__setAnimationPromise(
        texture,
        WallAnimations.wallLight,
        events,
        loop,
      ),
    };
  }

  _setupLeafAnimations(position) {
    const texture = this.createSpine(resourceHelper.get(sharedSourceNames.leaf));
    this.setPosition(texture, position);
    this.__animations = {
      ...this.__animations,
      [LeafAnimations.animation]: (events, loop = false) => this.__setAnimationPromise(
        texture,
        LeafAnimations.animation,
        events,
        loop,
      ),
    };
    return texture;
  }

  _setupFishAnimations(texture) {
    this.__animations = {
      ...this.__animations,
      [FishAnimations.fish1]: (events, loop = false) => this.__setAnimationPromise(
        texture,
        FishAnimations.fish1,
        events,
        loop,
      ),
      [FishAnimations.fish2]: (events, loop = false) => this.__setAnimationPromise(
        texture,
        FishAnimations.fish2,
        events,
        loop,
      ),
      [FishAnimations.fish3]: (events, loop = false) => this.__setAnimationPromise(
        texture,
        FishAnimations.fish3,
        events,
        loop,
      ),
      [FishAnimations.fish4]: (events, loop = false) => this.__setAnimationPromise(
        texture,
        FishAnimations.fish4,
        events,
        loop,
      ),
    };
  }

  __setupFishRandomAnimationChain() {
    const animationNames = [FishAnimations.fish1, FishAnimations.fish2, FishAnimations.fish3, FishAnimations.fish4];
    let index = random(0, animationNames.length);
    if (index === this.__fish_animationIndex) {
      index = index - 1 < 0 ? index + 1 : index - 1;
    }
    this.__fish_animationIndex = index;
    this.__animations[animationNames[index]]().then(() => {
      this.__setupFishRandomAnimationChain();
    });
  }

  setMultiplierAnimation(response, multiplierConfigs) {
    const oddText = this.createText(response, {
      ...multiplierValueStyle,
      fontSize: 100,
    });

    this.setPosition(oddText, multiplierConfigs.oddStart);
    oddText.anchor.set(.5);
    oddText.scale.set(0);

    return new Promise((resolve) => {
      new TimelineLite()
        .to(oddText,
          {
            delay: .2,
            duration: .1,
            pixi: { scale: multiplierConfigs.oddScale },
          })
        .to(oddText,
          {
            duration: .3,
            delay: .5,
            ...multiplierConfigs.oddEnd,
            onComplete: () => {
              MULTIPLIER_STATE.next(MULTIPLIER_STATE.getValue() + response);
              oddText.visible = false;
              resolve(true);
            },
          });
      this.container[GameLayers.winOdd].addChild(oddText);
    });

  }

  setWinOdd(response, {
    position = {
      x: mobileConfig.width / 2,
      y: 500,
    },
    winOddConfigs,
  } = {}) {
    const winOddContainer = this.createContainer('winOddContainer');
    const oddText = this.createText(`X${response.odd}`, {
      ...winOddTextStyle,
      fontSize: winOddConfigs.oddFontSize,
    });
    const winText = this.createText(`WIN: ${getAmount(response.winAmount)}`, {
      ...winOddTextStyle,
      fontSize: winOddConfigs.winFontSize,
    });
    const backgroundShadow = this.createSprite(resourceHelper.get(sharedSourceNames.backgroundShadow));

    oddText.anchor.set(.5);
    winText.anchor.set(.5);
    backgroundShadow.anchor.set(.5);
    backgroundShadow.scale.set(0);

    this.setPosition(winOddContainer, position);
    this.setPosition(backgroundShadow, { y: -45 });
    this.setPosition(oddText, { y: winOddConfigs.oddStart });
    this.setPosition(winText, { y: winOddConfigs.winStart });

    winOddContainer.addChild(backgroundShadow, oddText, winText);

    oddText.alpha = 0;
    winText.alpha = 0;


    return new Promise((resolve) => {
      gsap.to(backgroundShadow, {
        delay: .4,
        duration: .05,
        pixi: { scale: winOddConfigs.shadowScale },
      });

      new TimelineLite()
        .to(oddText, { duration: .5, alpha: 1, delay: .3, y: winOddConfigs.oddEnd })
        .to(oddText, { duration: 1, alpha: 0, delay: .2 });

      new TimelineLite()
        .to(winText, { duration: .5, alpha: 1, delay: .7, y: winOddConfigs.winEnd, onComplete: () => resolve(true) })
        .to(winText, { duration: 1, alpha: 0, delay: .2 })
        .to(backgroundShadow, { duration: 1, alpha: 0, delay: -.7 });

      this.container[GameLayers.winOdd].addChild(winOddContainer);
    });
  }

  getUserIdAndDeposit({ w = 160, h = 80, icon, m = 3, freeBetIcon } = {}) {
    const nativeW = w;
    w = freeBetIcon ? w + freeBetIcon.width + m : w;
    const container = this.createContainer('Deposit');

    const roundedRect = this.createGraphics()
      .beginFill(0x000000)
      .drawRoundedRect(0, 0, w + 2 * m, h + 2 * m, 26)
      .endFill();

    const depositRect = this.createGraphics()
      .beginFill(0x37a718)
      .drawRoundedRect(m, h / 2 - 5 + m, nativeW, h / 2 + 5, h / 2)
      .endFill();
    const radius = (h / 2 + 5) / 2;

    this.setupButtonMode(depositRect);
    this.registerClick(depositRect, () => {
      // soundService.playSingleClick();
      iframeService.openDeposit();
    });

    const plusContainer = this.createGraphics()
      .beginFill(0x38c313)
      .drawCircle(radius + m, h / 2 - 5 + m + radius, radius)
      .endFill();
    roundedRect.alpha = .5;
    const depositTitle = new this.PIXI.Text(translationKeys.deposit, {
      fill: 0xFFFFFF,
      fontSize: 20,
      align: 'center',
    });
    depositTitle.anchor.set(.5);

    this.setPosition(depositTitle, { x: nativeW / 2 + radius, y: (h + depositTitle.height) / 2 + 5 + m });
    const userIdTitle = new this.PIXI.Text('ID', {
      fill: 0xEEEEEE,
      fontSize: 20,
      align: 'center',
    });
    userIdTitle.anchor.set(.5);
    this.setPosition(userIdTitle, { x: nativeW / 2, y: userIdTitle.height / 2 + 5 + m });
    this.addSubscriber(USER_DATA.subscribe((data) => {
      if (data && data.id) {
        userIdTitle.text = `ID ${data.id}`;
      }
    }, true));
    if (icon) {
      icon.anchor.set(.5);
      icon.scale.set(.8);
      this.setPosition(icon, { x: radius + m, y: h / 2 - 5 + radius + m });
      plusContainer.addChild(icon);
    }

    container.addChild(
      roundedRect,
      depositRect,
      plusContainer,
      userIdTitle,
      depositTitle,
    );
    container.size = container.width;

    if (freeBetIcon) {
      const countRect = this.createGraphics()
        .beginFill(0xf3bd0d)
        .drawRoundedRect(
          nativeW + freeBetIcon.width / 2 + 10, -5, 48, 18, 9,
        )
        .endFill();
      const countTitle = new this.PIXI.Text('', {
        fontFamily: FONT_LATO_NAME,
        fontSize: 14,
        fontWeight: 600,
        fill: 0x010101,
      });
      freeBetIcon.anchor.set(.5);
      countTitle.anchor.set(.5);
      countRect.addChild(countTitle);
      this.setPosition(freeBetIcon, { x: nativeW + freeBetIcon.width / 2 + 2 * m, y: h / 2 + m });
      this.setPosition(countTitle, { x: nativeW + freeBetIcon.width / 2 + 35, y: 3 });
      container.addChild(
        freeBetIcon,
        countRect,
      );
      container.size = container.width - 20;
      this.addSubscriber(FREE_SPIN_COUNT_STATE.subscribe(({ length }) => {
        if (length) {
          countTitle.text = `x${length}`;
        } else {
          countRect.alpha = 0;
        }
      }, true));
    }

    return container;
  }

  setBallBreakWin(
    {
      odd,
      turn,
      length,
      side,
      position = {},
      positionMistake = { x: 0, y: 0 },
      winOddConfigs,
    } = {},
  ) {
    const winOddContainer = this.createContainer('winOddContainer');
    const oddText = this.createText(`x${odd}`, {
      ...bonusOddStyle,
    });

    const leafPosition = {
      x: position.x,
      y: position.y - winOddConfigs.leafMistake,
    };

    const leafSpine = this._setupLeafAnimations(leafPosition);

    this.setPosition(winOddContainer, {
      x: 0,
      y: 0,
    });

    this.setPosition(oddText, position);

    oddText.anchor.set(.5);

    leafSpine.scale.set(winOddConfigs.leafScale);
    oddText.scale.set(0);

    winOddContainer.addChild(leafSpine, oddText);

    return new Promise((resolve) => {
      gameAnimations.leafAnimation().then(() => {
        soundService.playMoveSound();
        new TimelineLite()
          .to(leafSpine,
            {
              duration: .3,
              delay: .2 * turn,
              x: winOddConfigs.containerPosition.x,
              y: 240,
              alpha: 0,
            });
      });
      new TimelineLite()
        .to(oddText,
          { duration: .3, delay: .8, y: position.y - positionMistake.y, pixi: { scale: winOddConfigs.textScale } })
        .to(oddText,
          {
            duration: .3,
            delay: .2 * turn + .55,
            x: winOddConfigs.containerPosition.x,
            y: 200,
            onComplete: () => {
              oddText.visible = false;
              leafSpine.visible = false;
              const nextValue = getFixedValue(BONUS_TOTAL_ODD.getValue() + odd);
              BONUS_TOTAL_ODD.next(nextValue);
              resolve(true);
            },
          });
      this.container[GameLayers.winOdd].addChild(winOddContainer);
    });
  }

  _setDragonSoulAnimationTexture({ name, position }) {
    const texture = this.createSpine(resourceHelper.get(name));
    this.setPosition(texture, position);
    this.container[GameLayers.dragonSoulSpine].visible = false;
    this._setupDragonSoulAnimations(texture);
    this.container[GameLayers.dragonSoulSpine].addChild(texture);
  }

  _setBreakingBallSpineTexture({ name, position }) {
    const texture = this.createSpine(resourceHelper.get(name));
    this.setPosition(texture, position);
    this.container[GameLayers.breakingBallSpine].visible = false;
    this._setupBreakingBallAnimations(texture);
    this.container[GameLayers.breakingBallSpine].addChild(texture);
  }

  _setParticlesSpineTexture({ name, position }) {
    const texture = this.createSpine(resourceHelper.get(name));
    this.setPosition(texture, position);
    this.container[GameLayers.particles].visible = false;
    this._setupParticlesAnimations(texture);
    this.container[GameLayers.particles].addChild(texture);
  }

  _setFishAnimationTexture({ name, position }) {
    const texture = this.createSpine(resourceHelper.get(name));
    this.setPosition(texture, position);
    this._setupFishAnimations(texture);
    this.__setupFishRandomAnimationChain();
    this.container[GameLayers.fishSpine].addChild(texture);
  }


  _getBreakingBallTexture({ name, position }, configs) {
    const texture = this.createSpine(resourceHelper.get(name));
    texture.visible = false;
    this.setPosition(this.container[GameLayers.allBallsBreakingSpine],
      configs.boomAnimationPosition);
    this.setPosition(texture, position);
    this._breakCustomBallBoom();
    this.container[GameLayers.allBallsBreakingSpine].addChild(texture);
    return texture;
  }

  _breakCustomBallBoom() {
    this.__animations = {
      ...this.__animations,
      breakCustomBallBoom: (index, events, loop = false) => {
        this.container[GameLayers.allBallsBreakingSpine].visible = true;
        const texture = this.container[GameLayers.allBallsBreakingSpine].children[index];
        texture.index = index;
        texture.visible = true;
        return this.__setAnimationPromise(
          texture,
          'animation',
          events,
          loop,
        ).then(e => {
          // this.container[GameLayers.breakingBallSpine].visible = false;
          return e;
        });
      },
    };
  }

  // _setAllBallsBreakingSpineAnimation({ name, position }, events, loop = false) {
  //   this.container[GameLayers.allBallsBreakingSpine].visible = false;
  //   return this.__setAnimationPromise(
  //     texture,
  //     'animation',
  //     events,
  //     loop,
  //   ).then((e) => {
  //     // this.container[GameLayers.breakingBallSpine].visible = false;
  //     return e;
  //   })
  // }

}
